/* =====================================================================
 * AdminPage — 관리자 페이지 (admin+ 전용)
 *
 * 팀 목록 / 팀 생성 모달 / 팀 상세(멤버 관리 · 토큰 배분)
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
  Plus, ArrowLeft, Trash2, Users, Coins, HardDrive,
  Loader2, Link2, Copy, PanelLeft, Search,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight,
} from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useDMConversationStore } from '@auw/stores';
import { Button } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Badge } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { useTeamStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import {
  createTeam, updateTeam, deleteTeam,
  removeTeamMember, allocateTokens, reclaimMemberTokens,
  leaveTeam, listTeamLeaveLogs,
  createTeamInviteLink, listTeamInviteLinks, deactivateTeamInviteLink, deleteTeamInviteLink,
  type TeamInfo, type TeamMember, type TeamInviteLink, type TeamMembershipEvent,
} from '@auw/api-client';
import { formatTokens } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';

/** 표시할 페이지 번호 배열 (최대 7개, 줄임표 포함) */
function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */

type View = 'list' | 'detail';

/** 팀당 생성 가능한 초대 링크 최대 개수 (백엔드 MAX_TEAM_INVITE_LINKS 와 동기화) */
const MAX_INVITE_LINKS = 20;

export const AdminPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const addToast = useChatStore((s) => s.addToast);
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const { teams, loadMyTeams } = useTeamStore();
  const { currentDetail, loadTeamDetail, clearDetail } = useTeamStore();

  const [view, setView] = useState<View>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /* P11-5j: gate the "팀 생성" CTA via `admin.teams.create`. The
   * surrounding card and team list still render so admins can manage
   * existing teams. */
  const teamsCreateEnabled = useFeatureFlags().isEnabled('admin.teams.create');

  useEffect(() => { loadMyTeams(); }, [loadMyTeams]);

  const openDetail = useCallback(async (teamId: string) => {
    setLoading(true);
    await loadTeamDetail(teamId);
    setView('detail');
    setLoading(false);
  }, [loadTeamDetail]);

  const backToList = useCallback(() => {
    clearDetail();
    setView('list');
    loadMyTeams();
  }, [clearDetail, loadMyTeams]);

  return (
    <Wrap>
      {!sidebarOpen && (
        <SidebarToggleSlot>
          <SidebarToggleBtn onClick={() => setSidebarOpen(true)} title={t('common:storage.openSidebar')}>
            <PanelLeft size={18} />
          </SidebarToggleBtn>
        </SidebarToggleSlot>
      )}

      {view === 'list' && (
        <Card>
          <CardHeader>
            <div>
              <CardTitle>{t('admin:team.title')}</CardTitle>
              <CardDesc>{t('admin:team.description')}</CardDesc>
            </div>
            {teamsCreateEnabled && (
              <CreateBtn onClick={() => setCreateOpen(true)}>
                <Plus size={16} /> {t('admin:team.create')}
              </CreateBtn>
            )}
          </CardHeader>

          {teams.length === 0 ? (
            <EmptyState>
              <Users size={40} strokeWidth={1.2} />
              <span>{t('admin:team.noTeams')}</span>
            </EmptyState>
          ) : (
            <TeamGrid>
              {teams.map((team) => {
                const tokenRemain = Math.max(0, team.tokens_total - team.tokens_used);
                const tokenPct = team.tokens_total > 0 ? (tokenRemain / team.tokens_total) * 100 : 0;
                const avatarColor = TEAM_COLORS[team.name.charCodeAt(0) % TEAM_COLORS.length];
                return (
                  <TeamCard key={team.team_id} onClick={() => openDetail(team.team_id)}>
                    <TeamCardTop>
                      <TeamAvatar style={{ background: avatarColor }}>
                        {team.name.charAt(0).toUpperCase()}
                      </TeamAvatar>
                      <TeamCardTitleArea>
                        <TeamCardName>{team.name}</TeamCardName>
                        {team.my_role && (
                          <TeamRoleBadge $isAdmin={team.my_role === 'admin'}>
                            {team.my_role === 'admin' ? t('admin:team.admin') : t('admin:team.member')}
                          </TeamRoleBadge>
                        )}
                      </TeamCardTitleArea>
                    </TeamCardTop>
                    {team.description && <TeamCardDesc>{team.description}</TeamCardDesc>}
                    <TeamStatsRow>
                      <TeamStat>
                        <TeamStatIcon><Users size={14} /></TeamStatIcon>
                        <TeamStatContent>
                          <TeamStatValue>{t('admin:team.membersCount', { count: team.member_count })}</TeamStatValue>
                          <TeamStatLabel>{t('admin:team.member')}</TeamStatLabel>
                        </TeamStatContent>
                      </TeamStat>
                      <TeamStatDivider />
                      <TeamStat>
                        <TeamStatIcon><Coins size={14} /></TeamStatIcon>
                        <TeamStatContent>
                          <TeamStatValue>{formatTokens(tokenRemain)}</TeamStatValue>
                          <TeamStatLabel>{t('admin:team.remainingTokens')}</TeamStatLabel>
                        </TeamStatContent>
                      </TeamStat>
                      <TeamStatDivider />
                      <TeamStat>
                        <TeamStatIcon><HardDrive size={14} /></TeamStatIcon>
                        <TeamStatContent>
                          <TeamStatValue>{formatGB(team.storage_used_bytes)} GB</TeamStatValue>
                          <TeamStatLabel>{t('admin:team.storageLabel')}</TeamStatLabel>
                        </TeamStatContent>
                      </TeamStat>
                    </TeamStatsRow>
                    <TeamProgressWrap>
                      <TeamProgressHeader>
                        <TeamProgressLabel>{t('admin:team.tokenBalance')}</TeamProgressLabel>
                        <TeamProgressPct $pct={tokenPct}>{Math.round(tokenPct)}%</TeamProgressPct>
                      </TeamProgressHeader>
                      <TokenBar><TokenBarFill $pct={tokenPct} /></TokenBar>
                    </TeamProgressWrap>
                    {team.created_at && (
                      <TeamCardFooter>
                        {t('admin:team.createdAt', { date: new Date(team.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) })}
                      </TeamCardFooter>
                    )}
                  </TeamCard>
                );
              })}
            </TeamGrid>
          )}
        </Card>
      )}

      {view === 'detail' && currentDetail && (
        <TeamDetailView
          detail={currentDetail}
          onBack={backToList}
          onRefresh={() => openDetail(currentDetail.team.team_id)}
          addToast={addToast}
        />
      )}

      {loading && (
        <LoadingOverlay><Loader2 size={24} className="spin" /></LoadingOverlay>
      )}

      {createOpen && (
        <CreateTeamModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); loadMyTeams(); }}
          addToast={addToast}
        />
      )}
    </Wrap>
  );
};

/* ------------------------------------------------------------------ */
/* Create Team Modal                                                   */
/* ------------------------------------------------------------------ */

const CreateTeamModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
  addToast: (t: any) => void;
}> = ({ onClose, onCreated, addToast }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await createTeam({
        name: name.trim(),
        description: desc.trim(),
      });
      addToast({ type: 'success', title: t('admin:team.createSuccess'), description: t('admin:team.createSuccessDesc', { name }) });
      onCreated();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.createFail'), description: err instanceof Error ? err.message : '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} title={t('admin:team.newTeam')} width="460px">
      <FieldGroup>
        <FieldLabel>{t('admin:team.teamName')}</FieldLabel>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('admin:team.namePlaceholder')} />
      </FieldGroup>
      <FieldGroup>
        <FieldLabel>{t('admin:team.descriptionLabel')}</FieldLabel>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t('admin:team.descPlaceholder')} />
      </FieldGroup>
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>{t('admin:team.cancel')}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!name.trim() || submitting} isLoading={submitting}>
          {t('admin:team.createBtn')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Team Detail View                                                    */
/* ------------------------------------------------------------------ */

const TeamDetailView: React.FC<{
  detail: { team: TeamInfo; members: TeamMember[] };
  onBack: () => void;
  onRefresh: () => void | Promise<void>;
  addToast: (t: any) => void;
}> = ({ detail, onBack, onRefresh, addToast }) => {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { team, members } = detail;
  const currentUserId = useUserStore((s) => s.user?.userId || '');
  const usage = useUserStore((s) => s.usage);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const setWorkspaceView = useTeamStore((s) => s.setWorkspaceView);
  const loadWorkspaceItems = useChatStore((s) => s.loadFromBackendApi);
  const loadDMConversations = useDMConversationStore((s) => s.loadConversations);
  const isOwner = team.owner_id === currentUserId;
  const currentMember = members.find((m) => m.user_id === currentUserId);
  const isAdmin = isOwner || currentMember?.role === 'admin';
  const canSelfLeave = !isOwner && currentMember?.role === 'member';
  /* P11-5j: granular team-management gates. The Team Pool UI tracks
   * `admin.teams.tokenPool`, the invite-link panel tracks
   * `admin.teams.inviteLinks`, and the per-member allocation column
   * tracks `admin.teams.memberTokenAllocation`. */
  const teamFlags = useFeatureFlags();
  const tokenPoolEnabled = teamFlags.isEnabled('admin.teams.tokenPool');
  const inviteLinksEnabled = teamFlags.isEnabled('admin.teams.inviteLinks');
  const memberAllocEnabled = teamFlags.isEnabled('admin.teams.memberTokenAllocation');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [removeConfirmTarget, setRemoveConfirmTarget] = useState<TeamMember | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [leaveLogs, setLeaveLogs] = useState<TeamMembershipEvent[]>([]);
  const [leaveLogsLoading, setLeaveLogsLoading] = useState(false);

  /* ---- invite links ---- */
  const [inviteLinks, setInviteLinks] = useState<TeamInviteLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);

  const loadInviteLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const res = await listTeamInviteLinks(team.team_id);
      setInviteLinks(res.links);
    } catch {
      setInviteLinks([]);
    } finally {
      setLinksLoading(false);
    }
  }, [team.team_id]);

  useEffect(() => {
    loadInviteLinks();
  }, [loadInviteLinks]);

  const loadLeaveLogs = useCallback(async () => {
    if (!isAdmin) {
      setLeaveLogs([]);
      return;
    }
    setLeaveLogsLoading(true);
    try {
      const res = await listTeamLeaveLogs(team.team_id, 20);
      setLeaveLogs(res.logs);
    } catch {
      setLeaveLogs([]);
    } finally {
      setLeaveLogsLoading(false);
    }
  }, [isAdmin, team.team_id]);

  useEffect(() => {
    void loadLeaveLogs();
  }, [loadLeaveLogs]);

  const handleDeleteTeam = async () => {
    setDeleting(true);
    try {
      await deleteTeam(team.team_id);
      addToast({ type: 'success', title: t('admin:team.deleteSuccess'), description: t('admin:team.deleteSuccessDesc', { name: team.name }) });
      setDeleteConfirmOpen(false);
      onBack();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.deleteFail'), description: err instanceof Error ? err.message : '' });
    } finally {
      setDeleting(false);
    }
  };

  const handleLeaveTeam = async () => {
    if (!canSelfLeave) return;
    setLeaving(true);
    try {
      const result = await leaveTeam(team.team_id);
      addToast({
        type: 'success',
        title: t('admin:team.leaveSuccess'),
        description: result.reclaimed_tokens > 0
          ? t('admin:team.leaveSuccessReclaimDesc', { tokens: formatTokens(result.reclaimed_tokens) })
          : t('admin:team.leaveSuccessDesc', { name: team.name }),
      });
      setLeaveConfirmOpen(false);
      setWorkspaceView('personal');
      await Promise.all([
        loadMyTeams(),
        loadWorkspaceItems(),
        loadDMConversations({ force: true }),
      ]);
      onBack();
    } catch (err) {
      addToast({
        type: 'error',
        title: t('admin:team.leaveFail'),
        description: err instanceof Error ? err.message : '',
      });
    } finally {
      setLeaving(false);
    }
  };

  /* ---- edit team pool ---- */
  const [editingPool, setEditingPool] = useState(false);
  const [editTokens, setEditTokens] = useState('');

  const savePool = async () => {
    const chargeTokens = parseInt(editTokens, 10) || 0;
    const currentTokensTotal = Math.max(0, team.tokens_total);
    const adminPersonalRemain = Math.max(0, usage.tokensLimit - usage.tokensUsed);
    const requiredCharge = Math.max(0, chargeTokens);

    if (chargeTokens <= 0) {
      addToast({
        type: 'warning',
        title: t('admin:team.chargeTokensWarning'),
      });
      return;
    }
    if (requiredCharge > adminPersonalRemain) {
      addToast({
        type: 'warning',
        title: t('admin:team.insufficientTokens'),
        description: t('admin:team.insufficientTokensDesc', { required: formatTokens(requiredCharge), available: formatTokens(adminPersonalRemain) }),
      });
      return;
    }

    try {
      await updateTeam(team.team_id, {
        tokens_total: currentTokensTotal + requiredCharge,
      });
      addToast({ type: 'success', title: t('admin:team.chargeSuccess') });
      setEditingPool(false);
      setEditTokens('');
      await onRefresh();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.chargeFail'), description: err instanceof Error ? err.message : '' });
    }
  };

  const buildInviteUrl = useCallback((inviteToken: string) => {
    return `${window.location.origin}/invite/team/${encodeURIComponent(inviteToken)}`;
  }, []);

  const handleCreateInviteLink = async () => {
    if (!isOwner) {
      addToast({ type: 'error', title: t('admin:team.noPermission'), description: t('admin:team.onlyOwnerCanInvite') });
      return;
    }
    if (inviteLinks.length >= MAX_INVITE_LINKS) {
      addToast({
        type: 'error',
        title: t('admin:team.inviteLimitReached', { max: MAX_INVITE_LINKS }),
        description: t('admin:team.inviteLimitReachedDesc'),
      });
      return;
    }
    setCreatingLink(true);
    try {
      const created = await createTeamInviteLink(team.team_id, {
        expires_hours: 24 * 7,
        max_uses: 200,
      });
      await loadInviteLinks();
      if (created.invite_token) {
        const inviteUrl = buildInviteUrl(created.invite_token);
        try {
          await navigator.clipboard.writeText(inviteUrl);
          addToast({ type: 'success', title: t('admin:team.inviteCreateSuccess'), description: t('admin:team.inviteCreateCopied') });
        } catch {
          addToast({ type: 'success', title: t('admin:team.inviteCreateSuccess'), description: t('admin:team.inviteCreateCopyManual') });
        }
      } else {
        addToast({ type: 'success', title: t('admin:team.inviteCreateSuccess') });
      }
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.inviteCreateFail'), description: err instanceof Error ? err.message : '' });
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyInviteLink = async (inviteToken: string | null) => {
    if (!inviteToken) return;
    try {
      await navigator.clipboard.writeText(buildInviteUrl(inviteToken));
      addToast({ type: 'success', title: t('admin:team.inviteCopySuccess') });
    } catch {
      addToast({ type: 'error', title: t('admin:team.copyFail'), description: t('admin:team.copyFailDesc') });
    }
  };

  const handleDeactivateInviteLink = async (inviteToken: string | null) => {
    if (!inviteToken) return;
    try {
      await deactivateTeamInviteLink(team.team_id, inviteToken);
      addToast({ type: 'success', title: t('admin:team.inviteDeactivateSuccess') });
      await loadInviteLinks();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.deactivateFail'), description: err instanceof Error ? err.message : '' });
    }
  };

  const handleDeleteInviteLink = async (inviteToken: string | null) => {
    if (!inviteToken) return;
    try {
      await deleteTeamInviteLink(team.team_id, inviteToken);
      addToast({ type: 'success', title: t('admin:team.inviteDeleteSuccess') });
      await loadInviteLinks();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.inviteDeleteFail'), description: err instanceof Error ? err.message : '' });
    }
  };

  const handleRemoveMember = async () => {
    if (!removeConfirmTarget) return;
    setRemovingMember(true);
    try {
      const result = await removeTeamMember(team.team_id, removeConfirmTarget.user_id);
      addToast({
        type: 'success',
        title: t('admin:team.memberRemoveSuccess'),
        description: result.reclaimed_tokens > 0
          ? t('admin:team.memberRemoveReclaimDesc', { tokens: formatTokens(result.reclaimed_tokens) })
          : t('admin:team.memberRemoveSuccessDesc', { name: removeConfirmTarget.name || removeConfirmTarget.email || t('admin:team.memberLabel') }),
      });
      setRemoveConfirmTarget(null);
      await onRefresh();
      await loadLeaveLogs();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.memberRemoveFail'), description: err instanceof Error ? err.message : '' });
    } finally {
      setRemovingMember(false);
    }
  };

  /* ---- token allocation ---- */
  const [allocModalTarget, setAllocModalTarget] = useState<TeamMember | null>(null);
  const [editTokenAmount, setEditTokenAmount] = useState('');

  /* ---- member search & pagination ---- */
  const MEMBERS_PER_PAGE = 10;
  const [memberSearch, setMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const q = memberSearch.trim().toLowerCase();
    return members.filter(
      (m) =>
        (m.name && m.name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q)),
    );
  }, [members, memberSearch]);

  const totalMemberPages = Math.max(1, Math.ceil(filteredMembers.length / MEMBERS_PER_PAGE));
  const safePage = Math.min(memberPage, totalMemberPages);
  const pagedMembers = filteredMembers.slice(
    (safePage - 1) * MEMBERS_PER_PAGE,
    safePage * MEMBERS_PER_PAGE,
  );

  useEffect(() => { setMemberPage(1); }, [memberSearch]);

  /* ---- invite link pagination ---- */
  const INVITE_LINKS_PER_PAGE = 10;
  const inviteLinkLimitReached = inviteLinks.length >= MAX_INVITE_LINKS;
  const [inviteLinkPage, setInviteLinkPage] = useState(1);

  const totalInviteLinkPages = Math.max(1, Math.ceil(inviteLinks.length / INVITE_LINKS_PER_PAGE));
  const safeInviteLinkPage = Math.min(inviteLinkPage, totalInviteLinkPages);
  const pagedInviteLinks = inviteLinks.slice(
    (safeInviteLinkPage - 1) * INVITE_LINKS_PER_PAGE,
    safeInviteLinkPage * INVITE_LINKS_PER_PAGE,
  );
  const inviteLinkPageNumbers = buildPageNumbers(safeInviteLinkPage, totalInviteLinkPages);

  useEffect(() => { setInviteLinkPage(1); }, [inviteLinks.length]);

  const saveTokenAlloc = async () => {
    if (!allocModalTarget) return;
    const amount = parseInt(editTokenAmount, 10) || 0;
    if (amount < 0) {
      addToast({ type: 'error', title: t('admin:team.allocMinError') });
      return;
    }
    if (amount < allocModalTarget.tokens_used) {
      addToast({
        type: 'error',
        title: t('admin:team.allocUnderUsedError'),
        description: t('admin:team.allocUsageDesc', { amount: formatTokens(allocModalTarget.tokens_used) }),
      });
      return;
    }
    const otherAllocated = members.reduce(
      (sum, m) => (m.user_id === allocModalTarget.user_id
        ? sum
        : sum + Math.max(0, m.tokens_allocated || 0)),
      0,
    );
    const allocatable = Math.max(0, (team.tokens_total || 0) - otherAllocated);
    if (amount > allocatable) {
      addToast({
        type: 'error',
        title: t('admin:team.allocOverPoolError'),
        description: t('admin:team.allocOverPoolDesc', { available: formatTokens(allocatable) }),
      });
      return;
    }

    try {
      await allocateTokens(team.team_id, allocModalTarget.user_id, amount);
      addToast({ type: 'success', title: t('admin:team.allocSuccess') });
      setAllocModalTarget(null);
      await onRefresh();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.allocFail'), description: err instanceof Error ? err.message : '' });
    }
  };

  const saveTokenReclaim = async (userId: string) => {
    const member = members.find((m) => m.user_id === userId);
    if (!member) {
      addToast({ type: 'error', title: t('admin:team.reclaimTargetNotFound') });
      return;
    }
    const reclaimable = Math.max(0, member.tokens_allocated - member.tokens_used);
    if (reclaimable <= 0) {
      addToast({ type: 'warning', title: t('admin:team.reclaimNone') });
      return;
    }

    try {
      await reclaimMemberTokens(team.team_id, userId, reclaimable);
      addToast({ type: 'success', title: t('admin:team.reclaimSuccess') });
      setAllocModalTarget(null);
      await onRefresh();
    } catch (err) {
      addToast({ type: 'error', title: t('admin:team.reclaimFail'), description: err instanceof Error ? err.message : '' });
    }
  };

  const allocatedTokenTotal = members.reduce(
    (sum, member) => sum + Math.max(0, member.tokens_allocated || 0),
    0,
  );
  const tokenRemain = Math.max(0, team.tokens_total - allocatedTokenTotal);
  const tokenPct = team.tokens_total > 0 ? (tokenRemain / team.tokens_total) * 100 : 0;
  const storageRemain = Math.max(0, team.storage_total_bytes - team.storage_used_bytes);
  const storageRemainPct = team.storage_total_bytes > 0 ? (storageRemain / team.storage_total_bytes) * 100 : 0;

  return (
    <Card>
      <BackRow onClick={onBack}><ArrowLeft size={16} /> {t('admin:team.backToList')}</BackRow>

      {/* ---- Team Info ---- */}
      <CardTitle>{team.name}</CardTitle>
      {team.description && <CardDesc>{team.description}</CardDesc>}

      {tokenPoolEnabled && (
        <TokenExpiryNote>
          {team.next_token_expiry
            ? t('admin:team.tokenExpiryActive', { date: formatExpiryDate(team.next_token_expiry, i18n.language) })
            : team.charge_expiry_preview
              ? t('admin:team.tokenExpiryPreview', { date: formatExpiryDate(team.charge_expiry_preview, i18n.language) })
              : t('admin:team.tokenExpiryPolicy')}
        </TokenExpiryNote>
      )}

      {isAdmin && tokenPoolEnabled && (
        <InviteLinkPanel>
          <InviteLinkGuide>{t('admin:team.chargeGuide')}</InviteLinkGuide>
          {!editingPool && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditTokens('');
                setEditingPool(true);
              }}
            >
              {t('admin:team.tokenCharge')}
            </Button>
          )}
        </InviteLinkPanel>
      )}
      <PoolSection>
        <PoolCard $kind="token">
          <PoolCardHeader>
            <PoolTitleGroup>
              <PoolIcon $kind="token"><Coins size={16} /></PoolIcon>
              <div>
                <PoolLabel>{t('admin:team.tokenPool')}</PoolLabel>
                <PoolSubLabel>{t('admin:team.tokenPoolSub')}</PoolSubLabel>
              </div>
            </PoolTitleGroup>
            <PoolPctBadge $pct={tokenPct}>{Math.round(tokenPct)}%</PoolPctBadge>
          </PoolCardHeader>
          <PoolValueRow>
            <PoolMainValue>{formatTokens(tokenRemain)}</PoolMainValue>
            <PoolTotalValue>/ {formatTokens(team.tokens_total)}</PoolTotalValue>
          </PoolValueRow>
          <PoolMetaRow>
            <span>{t('admin:team.allocated', { amount: formatTokens(allocatedTokenTotal) })}</span>
            <span>{t('admin:team.remaining', { amount: formatTokens(tokenRemain) })}</span>
          </PoolMetaRow>
          <PoolMeter>
            <PoolMeterFill $pct={tokenPct} />
          </PoolMeter>
        </PoolCard>
        <PoolCard $kind="storage">
          <PoolCardHeader>
            <PoolTitleGroup>
              <PoolIcon $kind="storage"><HardDrive size={16} /></PoolIcon>
              <div>
                <PoolLabel>{t('admin:team.storageLabel')}</PoolLabel>
                <PoolSubLabel>{t('admin:team.storageSub')}</PoolSubLabel>
              </div>
            </PoolTitleGroup>
            <PoolPctBadge $pct={storageRemainPct}>{Math.round(storageRemainPct)}%</PoolPctBadge>
          </PoolCardHeader>
          <PoolValueRow>
            <PoolMainValue>{formatGB(storageRemain)} GB</PoolMainValue>
            <PoolTotalValue>/ {formatGB(team.storage_total_bytes)} GB</PoolTotalValue>
          </PoolValueRow>
          <PoolMetaRow>
            <span>{t('admin:team.storageUsed', { amount: formatGB(team.storage_used_bytes) })}</span>
            <span>{t('admin:team.storageRemaining', { amount: formatGB(storageRemain) })}</span>
          </PoolMetaRow>
          <PoolMeter>
            <PoolMeterFill $pct={storageRemainPct} />
          </PoolMeter>
        </PoolCard>
      </PoolSection>
      {isAdmin && tokenPoolEnabled && editingPool && (
        <PoolEditRow>
          <Input
            type="text"
            inputMode="numeric"
            value={editTokens}
            onChange={(e) => setEditTokens(e.target.value.replace(/[^\d]/g, ''))}
            fullWidth={false}
            style={{ width: 160 }}
            placeholder={t('admin:team.tokenPlaceholder')}
          />
          <Button variant="primary" size="sm" onClick={savePool}>{t('admin:team.save')}</Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditingPool(false);
              setEditTokens('');
            }}
          >
            {t('admin:team.cancel')}
          </Button>
          {team.charge_expiry_preview && (
            <ChargePreviewHint>
              {t('admin:team.chargeExpiryPreview', { date: formatExpiryDate(team.charge_expiry_preview, i18n.language) })}
            </ChargePreviewHint>
          )}
        </PoolEditRow>
      )}

      {/* ---- Invite Links ---- */}
      {inviteLinksEnabled && (
        <>
          <SectionHeading>{t('admin:team.inviteLinks')}</SectionHeading>
          <InviteLinkPanel>
            <InviteLinkGuide>
              <Link2 size={14} />
              {isOwner
                ? t('admin:team.inviteGuideOwner')
                : t('admin:team.inviteGuideNonOwner')}
            </InviteLinkGuide>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCreateInviteLink}
              isLoading={creatingLink}
              disabled={!isOwner || creatingLink || inviteLinkLimitReached}
              title={inviteLinkLimitReached ? t('admin:team.inviteLimitReached', { max: MAX_INVITE_LINKS }) : undefined}
            >
              {t('admin:team.createInviteLink')}
            </Button>
          </InviteLinkPanel>
          {inviteLinkLimitReached && (
            <InviteLinkLimitNote>
              {t('admin:team.inviteLimitNote', { max: MAX_INVITE_LINKS })}
            </InviteLinkLimitNote>
          )}

      {linksLoading ? (
        <InviteLinkEmpty>{t('admin:team.inviteLinksLoading')}</InviteLinkEmpty>
      ) : inviteLinks.length === 0 ? (
        <InviteLinkEmpty>{t('admin:team.noInviteLinks')}</InviteLinkEmpty>
      ) : (
        <TableWrap style={{ marginTop: 10 }}>
          <Table>
            <thead>
              <Tr>
                <Th>{t('admin:team.linkColumn')}</Th>
                <Th $align="center">{t('admin:team.statusColumn')}</Th>
                <Th $align="center">{t('admin:team.usageColumn')}</Th>
                <Th $align="center">{t('admin:team.expiryColumn')}</Th>
                <Th $align="center" style={{ width: 150 }}>{t('admin:team.manageColumn')}</Th>
              </Tr>
            </thead>
            <tbody>
              {pagedInviteLinks.map((link) => (
                <Tr key={link.invite_link_id}>
                  <Td>
                    <InviteLinkText>
                      {link.invite_token ? buildInviteUrl(link.invite_token) : t('admin:team.hiddenLink')}
                    </InviteLinkText>
                  </Td>
                  <Td>
                    {link.status === 'active' && <Badge variant="success">{t('admin:team.statusActive')}</Badge>}
                    {link.status === 'revoked' && <Badge variant="default">{t('admin:team.statusRevoked')}</Badge>}
                    {link.status === 'expired' && <Badge variant="warning">{t('admin:team.statusExpired')}</Badge>}
                    {link.status === 'exhausted' && <Badge variant="error">{t('admin:team.statusExhausted')}</Badge>}
                  </Td>
                  <Td>{link.used_count} / {link.max_uses}</Td>
                  <Td>{link.expires_at ? new Date(link.expires_at).toLocaleString() : '—'}</Td>
                  <Td>
                    <InviteActionRow>
                      <SmallBtn onClick={() => handleCopyInviteLink(link.invite_token)}>
                        <Copy size={12} /> {t('admin:team.copy')}
                      </SmallBtn>
                      {link.status === 'active' ? (
                        <SmallBtn onClick={() => handleDeactivateInviteLink(link.invite_token)}>
                          {t('admin:team.deactivate')}
                        </SmallBtn>
                      ) : (
                        <SmallBtn $danger onClick={() => handleDeleteInviteLink(link.invite_token)}>
                          <Trash2 size={12} /> {t('admin:team.inviteDelete')}
                        </SmallBtn>
                      )}
                    </InviteActionRow>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {totalInviteLinkPages > 1 && (
        <InviteLinkPagination>
          <NumPageBtn disabled={safeInviteLinkPage === 1} onClick={() => setInviteLinkPage(1)} aria-label={t('admin:team.firstPage')}>
            <ChevronsLeft size={14} />
          </NumPageBtn>
          <NumPageBtn disabled={safeInviteLinkPage === 1} onClick={() => setInviteLinkPage(safeInviteLinkPage - 1)} aria-label={t('admin:team.prevPage')}>
            <ChevronLeft size={14} />
          </NumPageBtn>
          {inviteLinkPageNumbers.map((p, i) =>
            p === '...' ? (
              <PageEllipsis key={`e${i}`}>…</PageEllipsis>
            ) : (
              <NumPageBtn key={p} $active={p === safeInviteLinkPage} onClick={() => setInviteLinkPage(p as number)}>
                {p}
              </NumPageBtn>
            ),
          )}
          <NumPageBtn disabled={safeInviteLinkPage === totalInviteLinkPages} onClick={() => setInviteLinkPage(safeInviteLinkPage + 1)} aria-label={t('admin:team.nextPage')}>
            <ChevronRight size={14} />
          </NumPageBtn>
          <NumPageBtn disabled={safeInviteLinkPage === totalInviteLinkPages} onClick={() => setInviteLinkPage(totalInviteLinkPages)} aria-label={t('admin:team.lastPage')}>
            <ChevronsRight size={14} />
          </NumPageBtn>
        </InviteLinkPagination>
      )}
        </>
      )}

      {/* ---- Members Table ---- */}
      <MemberHeader>
        <SectionHeading style={{ margin: 0 }}>{t('admin:team.memberListTitle', { count: members.length })}</SectionHeading>
        <MemberSearchWrap>
          <MemberSearchIcon><Search size={14} /></MemberSearchIcon>
          <MemberSearchInput
            placeholder={t('admin:team.searchPlaceholder')}
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
          />
        </MemberSearchWrap>
      </MemberHeader>
      <TableWrap>
        <Table>
          <thead>
            <Tr>
              <Th>{t('admin:team.userColumn')}</Th>
              <Th>{t('admin:team.emailColumn')}</Th>
              <Th $align="center">{t('admin:team.roleColumn')}</Th>
              {memberAllocEnabled && <Th $align="center">{t('admin:team.allocTokenColumn')}</Th>}
              {memberAllocEnabled && <Th $align="center">{t('admin:team.usageTokenColumn')}</Th>}
              {isAdmin && memberAllocEnabled && <Th $align="center" style={{ width: 220 }}>{t('admin:team.reclaimColumn')}</Th>}
              {isAdmin && <Th $align="center" style={{ width: 80 }}>{t('admin:team.manageColumn')}</Th>}
            </Tr>
          </thead>
          <tbody>
            {pagedMembers.length === 0 ? (
              <Tr>
                <Td colSpan={(memberAllocEnabled ? 5 : 3) + (isAdmin ? (memberAllocEnabled ? 2 : 1) : 0)}>
                  <MemberEmptyRow>{t('admin:team.noResults')}</MemberEmptyRow>
                </Td>
              </Tr>
            ) : pagedMembers.map((m) => (
              <Tr key={m.user_id}>
                <Td><UserName>{m.name || '—'}</UserName></Td>
                <Td><EmailText>{m.email || '—'}</EmailText></Td>
                <Td $align="center">{m.role === 'admin' ? <Badge variant="info">{t('admin:team.admin')}</Badge> : <Badge variant="default">{t('admin:team.member')}</Badge>}</Td>
                {memberAllocEnabled && (
                  <Td $align="center">
                    {isAdmin ? (
                      <TokenClickable onClick={() => {
                        setAllocModalTarget(m);
                        setEditTokenAmount(String(m.tokens_allocated));
                      }}>
                        {formatTokens(m.tokens_allocated)}
                      </TokenClickable>
                    ) : (
                      <span style={{ fontSize: '0.8125rem' }}>{formatTokens(m.tokens_allocated)}</span>
                    )}
                  </Td>
                )}
                {memberAllocEnabled && (
                  <Td $align="center"><span style={{ fontSize: '0.8125rem' }}>{formatTokens(m.tokens_used)}</span></Td>
                )}
                {isAdmin && memberAllocEnabled && (
                  <Td $align="center">
                    <InlineTokenEdit>
                      <SmallBtn
                        onClick={() => saveTokenReclaim(m.user_id)}
                      >
                        {t('admin:team.recall')}
                      </SmallBtn>
                    </InlineTokenEdit>
                  </Td>
                )}
                {isAdmin && (
                  <Td $align="center">
                    {m.role !== 'admin' ? (
                      <RemoveBtn onClick={() => setRemoveConfirmTarget(m)}><Trash2 size={14} /></RemoveBtn>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      {totalMemberPages > 1 && (
        <Pagination>
          <PageBtn disabled={safePage <= 1} onClick={() => setMemberPage(safePage - 1)}>
            <ChevronLeft size={14} />
          </PageBtn>
          <PageInfo>{safePage} / {totalMemberPages}</PageInfo>
          <PageBtn disabled={safePage >= totalMemberPages} onClick={() => setMemberPage(safePage + 1)}>
            <ChevronRight size={14} />
          </PageBtn>
        </Pagination>
      )}
      <MemberTokenHint>
        <Coins size={13} />
        {t('admin:team.tokenHint')}
      </MemberTokenHint>

      {isAdmin && (
        <>
          <SectionHeading style={{ marginTop: 24 }}>{t('admin:team.leaveLogTitle')}</SectionHeading>
          {leaveLogsLoading ? (
            <LeaveLogEmpty>{t('admin:team.leaveLogLoading')}</LeaveLogEmpty>
          ) : leaveLogs.length === 0 ? (
            <LeaveLogEmpty>{t('admin:team.noLeaveLogs')}</LeaveLogEmpty>
          ) : (
            <TableWrap style={{ marginTop: 10 }}>
              <Table>
                <thead>
                  <Tr>
                    <Th>{t('admin:team.logUserColumn')}</Th>
                    <Th $align="center">{t('admin:team.logTypeColumn')}</Th>
                    <Th $align="center">{t('admin:team.logTimeColumn')}</Th>
                    <Th $align="center">{t('admin:team.logReclaimColumn')}</Th>
                    <Th $align="center">{t('admin:team.logGroupChatColumn')}</Th>
                    <Th $align="center">{t('admin:team.logDMColumn')}</Th>
                    <Th $align="center">{t('admin:team.logFileColumn')}</Th>
                    <Th $align="center">{t('admin:team.logStatusColumn')}</Th>
                  </Tr>
                </thead>
                <tbody>
                  {leaveLogs.map((log) => {
                    const totalFileChanges = (
                      (log.reassigned_team_file_count || 0)
                      + (log.reassigned_dm_file_count || 0)
                      + (log.deleted_dm_file_count || 0)
                    );
                    return (
                      <Tr key={log.event_id}>
                        <Td>
                          <UserName>{log.user_name || '—'}</UserName>
                          <LeaveLogMeta>{log.user_email || t('admin:team.noEmail')}</LeaveLogMeta>
                        </Td>
                        <Td $align="center">
                          {log.action === 'member_removed'
                            ? <Badge variant="warning">{t('admin:team.logAdminRemoved')}</Badge>
                            : <Badge variant="default">{t('admin:team.logSelfLeft')}</Badge>}
                        </Td>
                        <Td $align="center">
                          <span style={{ fontSize: '0.75rem' }}>
                            {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                          </span>
                        </Td>
                        <Td $align="center">{formatTokens(log.reclaimed_tokens || 0)}</Td>
                        <Td $align="center">{log.left_workspace_group_count || 0}</Td>
                        <Td $align="center">{log.left_dm_conversation_count || 0}</Td>
                        <Td $align="center">{totalFileChanges}</Td>
                        <Td $align="center">
                          {log.status === 'completed'
                            ? <Badge variant="success">{t('admin:team.logCompleted')}</Badge>
                            : <Badge variant="warning">{t('admin:team.logPartialFail')}</Badge>}
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </>
      )}

      {/* ---- 팀 나가기 / 팀 삭제 ---- */}
      {(canSelfLeave || isOwner) && (
        <DangerZone>
          {canSelfLeave && (
            <Button variant="danger" size="sm" onClick={() => setLeaveConfirmOpen(true)}>
              {t('admin:team.leaveTeam')}
            </Button>
          )}
          {isOwner && (
            <Button variant="danger" size="sm" onClick={() => setDeleteConfirmOpen(true)} leftIcon={<Trash2 size={14} />}>
              {t('admin:team.deleteTeam')}
            </Button>
          )}
        </DangerZone>
      )}

      {/* ---- 팀 나가기 확인 모달 ---- */}
      <Modal open={leaveConfirmOpen} onClose={() => setLeaveConfirmOpen(false)} title={t('admin:team.leaveModalTitle')} width="460px">
        <DeleteWarningText>{t('admin:team.leaveModalDesc', { name: team.name })}</DeleteWarningText>
        <LeaveConfirmList>
          <li>{t('admin:team.leaveItem1')}</li>
          <li>{t('admin:team.leaveItem2')}</li>
          <li>{t('admin:team.leaveItem3')}</li>
          <li>{t('admin:team.leaveItem4', { tokens: formatTokens(Math.max(0, (currentMember?.tokens_allocated || 0) - (currentMember?.tokens_used || 0))) })}</li>
          <li>{t('admin:team.leaveItem5')}</li>
        </LeaveConfirmList>
        <ModalActions>
          <Button variant="secondary" onClick={() => setLeaveConfirmOpen(false)}>{t('admin:team.cancel')}</Button>
          <Button variant="danger" onClick={handleLeaveTeam} disabled={leaving} isLoading={leaving}>
            {t('admin:team.leaveTeam')}
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        open={!!removeConfirmTarget}
        onClose={() => setRemoveConfirmTarget(null)}
        title={t('admin:team.removeModalTitle')}
        width="460px"
      >
        <DeleteWarningText>
          {removeConfirmTarget
            ? t('admin:team.removeModalDesc', { name: removeConfirmTarget.name || removeConfirmTarget.email || t('admin:team.memberLabel') })
            : t('admin:team.removeModalDescDefault')}
        </DeleteWarningText>
        <LeaveConfirmList>
          <li>{t('admin:team.removeItem1')}</li>
          <li>{t('admin:team.removeItem2')}</li>
          <li>{t('admin:team.removeItem3')}</li>
          <li>{t('admin:team.removeItem4')}</li>
          <li>{t('admin:team.removeItem5')}</li>
        </LeaveConfirmList>
        <ModalActions>
          <Button variant="secondary" onClick={() => setRemoveConfirmTarget(null)}>{t('admin:team.cancel')}</Button>
          <Button variant="danger" onClick={handleRemoveMember} disabled={removingMember} isLoading={removingMember}>
            {t('admin:team.removeMember')}
          </Button>
        </ModalActions>
      </Modal>

      {/* ---- 삭제 확인 모달 ---- */}
      <Modal open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} title={t('admin:team.deleteModalTitle')} width="400px">
        <DeleteWarningText>{t('admin:team.deleteWarning')}</DeleteWarningText>
        <DeleteConfirmText>{t('admin:team.deleteConfirm')}</DeleteConfirmText>
        <ModalActions>
          <Button variant="secondary" onClick={() => setDeleteConfirmOpen(false)}>{t('admin:team.cancel')}</Button>
          <Button variant="danger" onClick={handleDeleteTeam} disabled={deleting} isLoading={deleting}>
            {t('admin:team.deleteBtn')}
          </Button>
        </ModalActions>
      </Modal>

      <Modal
        open={!!allocModalTarget}
        onClose={() => setAllocModalTarget(null)}
        title={t('admin:team.allocModalTitle')}
        width="420px"
      >
        <FieldGroup>
          <FieldLabel>{t('admin:team.allocTargetUser')}</FieldLabel>
          <Input
            value={allocModalTarget ? `${allocModalTarget.name || '—'} (${allocModalTarget.email || t('admin:team.noEmail')})` : ''}
            disabled
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>{t('admin:team.allocTokenLabel')}</FieldLabel>
          <Input
            type="number"
            value={editTokenAmount}
            onChange={(e) => setEditTokenAmount(e.target.value)}
            placeholder={t('admin:team.allocTokenPlaceholder')}
          />
        </FieldGroup>
        {allocModalTarget && (
          <InviteLinkGuide>
            {t('admin:team.allocUsageDesc', { amount: formatTokens(allocModalTarget.tokens_used) })}
          </InviteLinkGuide>
        )}
        <ModalActions>
          <Button variant="secondary" onClick={() => setAllocModalTarget(null)}>{t('admin:team.cancel')}</Button>
          <Button variant="primary" onClick={saveTokenAlloc}>{t('admin:team.save')}</Button>
        </ModalActions>
      </Modal>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function formatGB(bytes: number): string {
  return (bytes / (1024 ** 3)).toFixed(1);
}

/** ISO 만료일을 로케일에 맞는 절대 날짜(YYYY. M. D.)로 변환. 파싱 실패 시 빈 문자열. */
function formatExpiryDate(iso?: string | null, locale?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const glassPanelStyles = css`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,18,0.82)' : 'rgba(255,255,255,0.9)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.58)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 36px rgba(0,0,0,0.28)'
      : '0 14px 32px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(24px) saturate(165%);
  -webkit-backdrop-filter: blur(24px) saturate(165%);
`;

const glassInsetStyles = css`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(30,30,30,0.78)' : 'rgba(255,255,255,0.84)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.52)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 10px 24px rgba(0,0,0,0.18)'
      : '0 8px 20px rgba(0,0,0,0.05)'};
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
`;

const Wrap = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 80px;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

const SidebarToggleSlot = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 5;
`;

const SidebarToggleBtn = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const Card = styled.main`
  width: 100%;
  max-width: 900px;
  margin: auto 0;
  ${glassPanelStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  padding: 36px 36px 32px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
`;

const CardTitle = styled.h1`
  margin: 0 0 4px;
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.bodyFontSize};
  }
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'transparent'};
  border-radius: 8px;
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'rgba(255,255,255,0.10)'
    : theme.colors.textPrimary};
  color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.textPrimary : theme.colors.bgPrimary};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: ${({ theme }) => theme.mode === 'dark' ? 'blur(12px)' : 'none'};
  transition: opacity 0.15s, background 0.15s;
  flex-shrink: 0;
  &:hover {
    opacity: ${({ theme }) => theme.mode === 'dark' ? 1 : 0.85};
    background: ${({ theme }) => theme.mode === 'dark'
      ? 'rgba(255,255,255,0.16)'
      : theme.colors.textPrimary};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

/* ---- Team Grid ---- */

const TEAM_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4'];

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const TeamCard = styled.button`
  text-align: left;
  padding: 24px;
  ${glassInsetStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  display: flex;
  flex-direction: column;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(38,38,38,0.86)' : 'rgba(255,255,255,0.92)'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.72)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 14px 30px rgba(0,0,0,0.24)'
        : '0 12px 28px rgba(0,0,0,0.08)'};
    transform: translateY(-2px);
  }
`;

const TeamCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TeamAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
`;

const TeamCardTitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TeamCardName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TeamRoleBadge = styled.span<{ $isAdmin: boolean }>`
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  flex-shrink: 0;
  background: ${({ $isAdmin, theme }) =>
    $isAdmin ? theme.colors.infoBg : theme.colors.accentLight};
  color: ${({ $isAdmin, theme }) =>
    $isAdmin ? theme.colors.info : theme.colors.textSecondary};
`;

const TeamCardDesc = styled.p`
  margin: 8px 0 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TeamStatsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 14px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TeamStat = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const TeamStatIcon = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const TeamStatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TeamStatValue = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const TeamStatLabel = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TeamStatDivider = styled.div`
  width: 1px;
  height: 28px;
  background: ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const TeamProgressWrap = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TeamProgressLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TeamProgressPct = styled.span<{ $pct: number }>`
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $pct, theme }) =>
    $pct < 20 ? theme.colors.usageBarCritical
    : $pct < 50 ? theme.colors.usageBarCaution
    : theme.colors.usageBarHealthy};
`;

const TokenBar = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const TokenBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 2px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $pct, theme }) =>
    $pct < 20 ? theme.colors.usageBarCritical
    : $pct < 50 ? theme.colors.usageBarCaution
    : theme.colors.usageBarHealthy};
  transition: width 0.3s;
`;

const TeamCardFooter = styled.div`
  margin-top: 12px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Back row ---- */

const BackRow = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

/* ---- Pool section ---- */

const PoolSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin: 12px 0 10px;
`;

const PoolCard = styled.div<{ $kind: 'token' | 'storage' }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  ${glassInsetStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  transition: border-color ${({ theme }) => theme.transitions.fast};
  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.68)'};
  }
`;

const PoolCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PoolTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PoolIcon = styled.div<{ $kind: 'token' | 'storage' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const PoolLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PoolSubLabel = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 1px;
`;

const PoolPctBadge = styled.span<{ $pct: number }>`
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $pct, theme }) =>
    $pct < 20 ? theme.colors.usageBarCritical
    : $pct < 50 ? theme.colors.usageBarCaution
    : theme.colors.usageBarHealthy};
`;

const PoolValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PoolMainValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

const PoolTotalValue = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const PoolMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const PoolMeter = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const PoolMeterFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 2px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $pct, theme }) =>
    $pct < 20 ? theme.colors.usageBarCritical
    : $pct < 50 ? theme.colors.usageBarCaution
    : theme.colors.usageBarHealthy};
  transition: width 0.3s ease;
`;

const PoolEditRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  padding: 10px 12px;
  ${glassInsetStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
`;

/* ---- Section heading ---- */

const SectionHeading = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 10px;
`;

/* ---- Invite Links ---- */

const InviteLinkPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const MemberTokenHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgTertiary};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  line-height: 1.4;

  svg { flex-shrink: 0; opacity: 0.6; }
`;

const InviteLinkGuide = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
`;

const InviteLinkEmpty = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  margin-top: 8px;
`;

const InviteLinkLimitNote = styled.div`
  margin-top: 6px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.warning};
`;

const TokenExpiryNote = styled.div`
  margin: -4px 0 14px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const ChargePreviewHint = styled.span`
  flex-basis: 100%;
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
`;

const LeaveLogEmpty = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  margin-top: 8px;
`;

const LeaveLogMeta = styled.div`
  margin-top: 4px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InviteLinkText = styled.code`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InviteActionRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* ---- Table ---- */

const TableWrap = styled.div`
  overflow-x: auto;
  ${glassInsetStyles};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const Th = styled.th<{ $align?: 'left' | 'center' | 'right' }>`
  text-align: ${({ $align = 'left' }) => $align};
  padding: 10px 14px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.72)'};
`;

const Td = styled.td<{ $align?: 'left' | 'center' | 'right' }>`
  padding: 10px 14px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  vertical-align: middle;
  text-align: ${({ $align = 'left' }) => $align};
  white-space: nowrap;
`;

const UserName = styled.span`
  font-weight: 500;
`;

const EmailText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

const TokenClickable = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline dashed;
  text-underline-offset: 2px;
  padding: 0;
  &:hover { opacity: 0.8; }
`;

const InlineTokenEdit = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const SmallBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.6875rem;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
  ${({ $danger, theme }) =>
    $danger &&
    css`
      border-color: ${theme.colors.error}55;
      color: ${theme.colors.error};
      &:hover {
        background: ${theme.colors.error}1a;
        border-color: ${theme.colors.error};
      }
    `}
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.error}20; color: ${({ theme }) => theme.colors.error}; }
`;


const FieldGroup = styled.div`
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;


/* ---- Danger Zone ---- */

const DangerZone = styled.div`
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;


const DeleteWarningText = styled.p`
  margin: 0 0 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const DeleteConfirmText = styled.p`
  margin: 0 0 4px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LeaveConfirmList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  line-height: 1.6;
`;

/* ---- Member search & pagination ---- */

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const MemberSearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const MemberSearchIcon = styled.span`
  position: absolute;
  left: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  pointer-events: none;
`;

const MemberSearchInput = styled.input`
  height: 32px;
  width: 200px;
  padding: 0 10px 0 30px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.62)'};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.82)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  outline: none;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.borderInput};
  }
`;

const MemberEmptyRow = styled.div`
  text-align: center;
  padding: 20px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
`;

const PageBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderInput};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const PageInfo = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: center;
`;

const InviteLinkPagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin-top: 10px;
`;

const NumPageBtn = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.75rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
          border-color: ${theme.colors.textPrimary};
          pointer-events: none;
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            border-color: ${theme.colors.border};
          }
        `}

  &:disabled {
    opacity: 0.35;
    cursor: default;
    pointer-events: none;
  }
`;

const PageEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  user-select: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(9,9,11,0.44)' : 'rgba(255,255,255,0.44)'};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 10;
  color: ${({ theme }) => theme.colors.textMuted};
`;
